import { useEffect, useMemo, useState } from 'react';
import type {
  FlowComposerMessage,
  FlowComposerProgressEvent,
} from '@shared/types/flow-composer';
import { useStickyChatScroll } from '@/lib/hooks/useStickyChatScroll';
import { ComposerInput } from './ComposerInput';
import { ComposerMessage } from './ComposerMessage';
import { ComposerQuickReplies } from './ComposerQuickReplies';
import { CheckProofLines } from './CheckProofLines';
import type { UsageSheetCheck, UsageSheetCheckTarget } from '@shared/types/forge-usage-sheet';

interface ComposerChatProps {
  messages: FlowComposerMessage[];
  loading: boolean;
  disabled?: boolean;
  placeholder: string;
  showInitialState: boolean;
  progress?: FlowComposerProgressEvent | null;
  onSend: (value: string) => void;
  /** Text to place in the message field (never sent automatically); a new
   * `key` re-applies the same text. */
  draftRequest?: { text: string; key: number } | null;
  /** Explicit stop of the answer in progress (absent until the turn can be stopped). */
  onStop?: () => void;
  stopping?: boolean;
  /** Live checks of the running framing turn (F2). */
  liveChecks?: { pending: UsageSheetCheckTarget[]; checks: UsageSheetCheck[] } | null;
}

export const ComposerChat = ({
  messages,
  loading,
  disabled = false,
  placeholder,
  showInitialState,
  progress,
  onSend,
  draftRequest = null,
  onStop,
  stopping = false,
  liveChecks = null,
}: ComposerChatProps) => {
  const [inputValue, setInputValue] = useState('');
  useEffect(() => {
    if (draftRequest) setInputValue(draftRequest.text);
  }, [draftRequest]);
  const {
    scrollRef: listRef,
    handleScroll: handleListScroll,
    resumeAutoScroll,
  } = useStickyChatScroll<HTMLDivElement>({
    watch: [messages, loading, progress?.label],
  });

  const lastAssistantSuggestions = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (!message || message.role !== 'assistant') continue;
      return message.suggestions ?? [];
    }
    return [];
  }, [messages]);

  // While streaming, the tail message is the "live" assistant bubble being filled by
  // deltas. The phases announced before the message (context, reasoning) stand in for it
  // and hide once it streams; every phase announced after it (the tool being assembled,
  // checks, wiring correction, preview) stays visible: the turn is not over even though
  // the message looks complete, and the chat and buttons are still disabled.
  const lastMessage = messages[messages.length - 1];
  const isStreaming =
    loading && lastMessage?.role === 'assistant' && lastMessage.content.length > 0;
  const announcedBeforeMessage = !progress || progress.stage === 'context' || progress.stage === 'reasoning';
  const showThinkingBubble = loading && (!isStreaming || !announcedBeforeMessage);

  const submit = () => {
    const value = inputValue.trim();
    if (!value || disabled || loading) return;
    resumeAutoScroll();
    onSend(value);
    setInputValue('');
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        ref={listRef}
        className="flex-1 overflow-y-auto p-6"
        onScroll={handleListScroll}
      >
        {showInitialState && (
          <div className="mt-2">
            <h1 className="font-display text-2xl font-semibold tracking-tight text-domino-ink">
              Créer dans la Forge
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-domino-inkSoft">
              Décrivez le besoin métier, les données à utiliser et le résultat attendu.
            </p>
          </div>
        )}

        <div className="mx-auto mt-4 flex max-w-3xl flex-col gap-3">
          {messages
            // The live (streaming) assistant message starts empty and gets filled by deltas.
            // While it's empty, the "thinking" bubble below stands in for it; skip rendering
            // an empty bubble so the user only sees one indicator at a time.
            .filter((message) => message.role === 'user' || message.content.length > 0)
            .map((message) => (
              <ComposerMessage key={message.id} message={message} />
            ))}

          {showThinkingBubble && (
            <div className="max-w-[90%] px-1 py-1 text-sm text-gray-600">
              <span className="inline-flex items-center gap-2">
                <span>{progress?.label ?? 'Domino réfléchit'}</span>
                <span className="inline-flex gap-1" aria-hidden="true">
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-400 motion-safe:animate-bounce [animation-delay:-0.3s]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-400 motion-safe:animate-bounce [animation-delay:-0.15s]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-400 motion-safe:animate-bounce" />
                </span>
              </span>
            </div>
          )}

          {loading && liveChecks && (
            <CheckProofLines checks={liveChecks.checks} pending={liveChecks.pending} open />
          )}

          {!loading && (
            <ComposerQuickReplies
              suggestions={lastAssistantSuggestions.slice(0, 2)}
              disabled={disabled}
              onSelect={(suggestion) => setInputValue(suggestion)}
            />
          )}
        </div>
      </div>

      <ComposerInput
        value={inputValue}
        placeholder={placeholder}
        disabled={disabled}
        loading={loading}
        onChange={setInputValue}
        onSubmit={submit}
        onStop={onStop}
        stopping={stopping}
      />
    </div>
  );
};
